import { defineStore } from "pinia";
import { useGeneralStore } from "%/dynamic-component-editor/stores/general-store";
import useFanPublicationStore from "%/dynamic-component-editor/stores/fan-publication-store";
import {
  useMyMonetizationLinkCollectionFeedStore,
} from "@/smart-feed/stores/feed/my-monetization-link-collection-feed-store";

export const useAffiliateLinkStore = defineStore("affiliate-link-store", {
  state: () => ({
    lastAffiliateLinkId: Number.MAX_SAFE_INTEGER,
    pageSize: 20,
    affiliateLinks: {},
    loadingCreateAffiliateLink: false,
    loadingAffiliateLinks: false,
    loadingEditAffiliateLink: false,
    affiliateLinkError: "",
    editingRowKey: 0,
    editingRowColumn: "",
    editingValue: "",
    createdAffiliateLink: {},
    loadingFetchAffiliateLinkCollections: false,
    loadingFetchCollectionsContainingLink: false,
    loadingAffiliateLinkCollection: false,
    loadingAddLinkToCollection: false,
    loadingRemoveLinkFromCollection: false,
    loadingSearchAffiliateLinkProducts: false,
    collections: [],
    collectionsContainingLink: [],
    collectionsWithSelectedStatusForLink: [],
    errorCreateMonetizationLinkCollection: "",
    errorAddLinkToCollection: "",
    errorRemoveLinkFromCollection: "",
    errorFetchCollectionsContainingLink: "",
    errorFetchOrgCollections: "",
    uploadedAffiliateLinkCollectionCoverUrl: "",
    affiliateLinkCollectionId: 0,
    affiliateLink: {},
    isAddToCollectionModalOpen: false,
    isUploadingAffiliateLinkProductImage: false,
    uploadingAffiliateLinkProductImageId: 0,
    isCreateLinkModalOpen: false,
    editingCollection: {},
    showCollectionModal: false,
    collectionsWithShortLinkCollectionStatus: {},
    activeCollectionId: null,
    selectedCollectionItem: null,
  }),
  getters: {
    hasMoreAffiliateLinks() {
      return this.lastAffiliateLinkId !== -1;
    },
    orgId() {
      return useGeneralStore().orgId;
    },
  },
  actions: {
    createLink(linkUrl, successHandler = () => {
    }, errorHandler = () => {
    }) {
      this.affiliateLinkError = null;
      this.loadingCreateAffiliateLink = true;
      this.apiPost(
        "/affiliate-link/create",
        {
          link_url: linkUrl,
          org_id: this.orgId,
        },
        data => {
          if (data.shortLinkData) {
            successHandler(data.shortLinkData);
            this.createdAffiliateLink = data.shortLinkData;
          } else {
            errorHandler("Unable to create tracking link. Please try again.");
            this.affiliateLinkError = "Unable to create tracking link. Please try again.";
          }
        },
        () => {
          errorHandler("Unable to create tracking link. Please try again.");
          this.affiliateLinkError = "Unable to create tracking link. Please try again.";
        },
        () => {
          this.loadingCreateAffiliateLink = false;
        },
      );
    },
    fetchAffiliateLinksByCollectionId(collectionId) {
      if (this.loadingAffiliateLinks) {
        return;
      }
      this.loadingAffiliateLinks = true;
      this.apiGet(
        `/affiliate-link/collection/${collectionId}/links`,
        {
          last_link_id: this.lastAffiliateLinkId,
        },
        data => {
          let loadedLinks = data.publisherAffiliateLinks;
          this.addLinksToAffiliateLinksCollection(loadedLinks, collectionId);
          if (data.publisherAffiliateLinks.length > 0) {
            this.lastAffiliateLinkId = Number(data.publisherAffiliateLinks.at(-1).publisher_affiliate_link_id);
          } else {
            this.lastAffiliateLinkId = -1;
          }
        },
        error => {
          this.lastAffiliateLinkId = -1;
          throw new Error(`Fetch Affiliate Links Error: ${error.message}`);
        },
        () => {
          this.loadingAffiliateLinks = false;
        },
      );
    },
    addLinksToAffiliateLinksCollection(loadedLinks, collectionId) {
      let collection = this.affiliateLinks[collectionId] ?? [];
      loadedLinks.forEach(link => {
        if (!collection.some(checkLink => checkLink.publisher_affiliate_link_id === link.publisher_affiliate_link_id)) {
          if (link.status === "active") {
            collection.push(link);
          }
        }
      });
      this.affiliateLinks[collectionId] = collection;
    },
    fetchAffiliateLinksByOrgId() {
      if (this.loadingAffiliateLinks) {
        return;
      }
      this.loadingAffiliateLinks = true;
      this.apiGet(
        `/affiliate-link/org/${this.orgId}/links`,
        {
          last_link_id: this.lastAffiliateLinkId,
          limit: 24,
        },
        data => {
          let loadedLinks = data.publisherAffiliateLinks;
          this.addLinksToAffiliateLinksCollection(loadedLinks, 0);
          if (data.publisherAffiliateLinks.length > 0) {
            this.lastAffiliateLinkId = Number(data.publisherAffiliateLinks.at(-1).publisher_affiliate_link_id);
          } else {
            this.lastAffiliateLinkId = -1;
          }
        },
        error => {
          throw new Error(`Fetch Affiliate Links Error: ${error.message}`);
        },
        () => {
          this.loadingAffiliateLinks = false;
        },
      );
    },
    fetchAddLinkToCollectionCollections(shortLinkId, overrideOrgId = null) {
      this.loadingFetchAffiliateLinkCollections = true;
      const orgIdToUse = overrideOrgId ?? this.orgId;
      this.collectionsWithShortLinkCollectionStatus[shortLinkId] = [];
      this.apiGet(
        `/affiliate-link-collection/org/${orgIdToUse}/item-status`,
        {
          last_id: 0,
          limit: 24,
          short_link_id: shortLinkId,
        },
        data => {
          this.collectionsWithShortLinkCollectionStatus[shortLinkId] = data;
        },
        () => {
        },
        () => {
          this.loadingFetchAffiliateLinkCollections = false;
        },
      );
    },
    fetchCollectionsWithSelectedStatusForLink(affiliateLinkId) {
      this.fetchMyOrgLinkCollections().then(() => {
        return this.fetchAllCollectionsContainingLink(affiliateLinkId);
      }).then(myLinkCollections => {
        this.collectionsWithSelectedStatusForLink = this.collections.map(collection => ({
          ...collection,
          selected: myLinkCollections.some(collectionContainingLink => {
            return collection.affiliate_link_collection_id === collectionContainingLink.affiliate_link_collection_id;
          }),
        }));
      });
    },
    editShortLinkProductDetails(
      index,
      shortLinkId,
      brandName,
      productName,
      linkDescription,
      notes,
      collectionId,
      advertiserName = "",
      successHandler = () => {},
      errorHandler = () => {},
    ) {
      if (this.loadingEditAffiliateLink) {
        return;
      }
      this.loadingEditAffiliateLink = true;
      this.apiPost(
        `/affiliate-link/${shortLinkId}/product-details`,
        {
          publication_id: useFanPublicationStore().publicationId,
          brand_name: brandName,
          product_name: productName,
          my_description: linkDescription,
          notes: notes,
          advertiser_name: advertiserName,
        },
        data => {
          const collectionLinks = this.affiliateLinks[collectionId] || {};
          const existingLink = collectionLinks[index] || {};
          this.affiliateLinks = {
            ...this.affiliateLinks,
            [collectionId]: {
              ...collectionLinks,
              [index]: {
                ...existingLink,
                ...data,
              },
            },
          };
          this.editingValue = "";
          this.editingRowColumn = "";
          this.editingRowKey = 0;
          successHandler(data);
        },
        error => {
          errorHandler(error);
          throw new Error(`Edit Affiliate Link Error: ${error.message}`);
        },
        () => {
          this.loadingEditAffiliateLink = false;
        },
      );
    },
    createMonetizationLinkCollection(orgId, name, description, coverImageUrl, shortLinkId, visibility) {
      this.loadingAffiliateLinkCollection = true;
      return this.apiPost(
        "/affiliate-link-collection/create",
        {
          org_id: orgId,
          publication_id: useFanPublicationStore().publicationId,
          name,
          description,
          cover_image_url: coverImageUrl,
          short_link_id: shortLinkId,
          visibility,
        },
        () => {
          useMyMonetizationLinkCollectionFeedStore().reload({ orgId: this.orgId });
        },
        () => {
          this.errorCreateMonetizationLinkCollection = "Unable to create collection. Please try again.";
        },
        () => {
          this.loadingAffiliateLinkCollection = false;
          this.showCollectionModal = false;
        },
      );
    },
    uploadAffiliateLinkCollectionCover(coverImageFile) {
      let formData = new FormData();
      formData.append("cover", coverImageFile);
      this.apiPost(
        "affiliate-link-collection/cover-image/upload",
        formData,
        data => {
          if (data) {
            this.uploadedAffiliateLinkCollectionCoverUrl = data;
          }
        },
      );
    },
    uploadAffiliateLinkProductImage(shortLinkId, productImageFile, successHandler = () => {
    }, errorHandler = () => {
    }) {
      const formData = new FormData();
      formData.append("product_image", productImageFile);
      formData.append("publication_id", useFanPublicationStore().publicationId);
      this.isUploadingAffiliateLinkProductImage = true;
      this.uploadingAffiliateLinkProductImageId = shortLinkId;
      return this.apiPost(
        `/affiliate-link/${shortLinkId}/product-image`,
        formData,
        response => {
          this.isUploadingAffiliateLinkProductImage = false;
          this.uploadingAffiliateLinkProductImageId = 0;
          successHandler(response);
        },
        error => {
          this.isUploadingAffiliateLinkProductImage = false;
          this.uploadingAffiliateLinkProductImageId = 0;
          errorHandler(error);
        },
      );
    },
    editAffiliateLinkCollection(collectionId, name, description, coverImageUrl, visibility) {
      this.loadingAffiliateLinkCollection = true;
      return this.apiPost(
        `/affiliate-link-collection/${collectionId}/update`,
        {
          name,
          description,
          cover_image_url: coverImageUrl,
          visibility,
        },
        () => {
          useMyMonetizationLinkCollectionFeedStore().reload({ orgId: this.orgId });
        },
        () => {
        },
        () => {
          this.loadingAffiliateLinkCollection = false;
          this.showCollectionModal = false;
          this.editingCollection = {};
        },
      );
    },
    addToAffiliateLinkCollection(collectionId, shortLinkId, successHandler = () => {}, errorHandler = () => {}) {
      this.loadingAddLinkToCollection = true;
      this.apiPost(
        `/affiliate-link-collection/${collectionId}/add`,
        {
          short_link_id: shortLinkId,
        },
        () => {
          successHandler();
        },
        error => {
          errorHandler(error);
        },
        () => {
          this.loadingAddLinkToCollection = false;
        },
      );
    },
    removeFromAffiliateLinkCollection(collectionId, shortLinkId, successHandler = () => {}, errorHandler = () => {}) {
      this.loadingRemoveLinkFromCollection = true;
      this.apiPost(
        `/affiliate-link-collection/${collectionId}/remove`,
        {
          short_link_id: shortLinkId,
        },
        () => {
          successHandler();
        },
        error => {
          errorHandler(error);
        },
        () => {
          this.loadingRemoveLinkFromCollection = false;
        },
      );
    },
    searchAffiliateLinkProducts(query) {
      this.loadingSearchAffiliateLinkProducts = true;

      return this.apiPost(
        "/affiliate-link/publisher-affiliate-link/search-or-create",
        {
          query,
          org_id: this.orgId,
        },
        data => {
          return data;
        },
        () => {
          this.errorCreateMonetizationLinkCollection = "Unable to search for products. Please try again.";
        },
        () => {
          this.loadingSearchAffiliateLinkProducts = false;
        },
      );
    },
  },
});
